import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class EscapeBackService implements OnDestroy {
  private subscription?: Subscription;
  private readonly entries: Array<{ id: number; url: string }> = [];
  private cursor = -1;
  private revision = 0;
  private navigating = false;
  private backPending = false;
  private navigation?: { restoredId?: number; popstate: boolean; replace: boolean; skip: boolean };
  private readonly keyContexts = new WeakMap<KeyboardEvent, { blocked: boolean; revision: number }>();
  private readonly timers = new Set<number>();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private readonly location: Location,
    private readonly dialog: MatDialog,
    private readonly zone: NgZone
  ) {}

  start(): void {
    const window = this.document.defaultView;
    if (this.subscription || !window) return;
    if (this.router.navigated) {
      const id = Number((this.location.getState() as { navigationId?: number })?.navigationId || 0);
      this.entries.push({ id, url: this.router.url });
      this.cursor = 0;
    }
    this.subscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        const extras = this.router.getCurrentNavigation()?.extras;
        this.revision++;
        this.navigating = true;
        this.navigation = { restoredId: event.restoredState?.navigationId,
          popstate: event.navigationTrigger !== 'imperative',
          replace: !!extras?.replaceUrl, skip: !!extras?.skipLocationChange };
      } else if (event instanceof NavigationEnd) {
        this.remember(event);
        this.navigating = false;
        this.backPending = false;
        this.navigation = undefined;
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.navigating = false;
        this.backPending = false;
        this.navigation = undefined;
      }
    });
    this.zone.runOutsideAngular(() => {
      window.addEventListener('keydown', this.captureKeydown, true);
      window.addEventListener('keydown', this.onKeydown);
    });
  }

  private remember(event: NavigationEnd): void {
    const navigation = this.navigation;
    if (navigation?.skip) return;
    const entry = { id: event.id, url: event.urlAfterRedirects };
    if (navigation?.popstate) {
      const index = this.entries.findIndex(row => row.id === navigation.restoredId);
      if (index < 0) {
        this.entries.splice(0, this.entries.length, entry);
        this.cursor = 0;
      } else {
        this.cursor = index;
        this.entries[index] = entry;
      }
    } else if (navigation?.replace && this.cursor >= 0) {
      this.entries[this.cursor] = entry;
    } else {
      this.entries.splice(this.cursor + 1);
      this.entries.push(entry);
      this.cursor = this.entries.length - 1;
    }
  }

  private plainEscape(event: KeyboardEvent): boolean {
    return event.key === 'Escape' && !event.repeat && !event.isComposing &&
      !event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey;
  }

  private hasBlockingOverlay(): boolean {
    if (this.dialog.openDialogs.length || this.document.fullscreenElement) return true;
    const nodes = this.document.querySelectorAll<HTMLElement>(
      '.cdk-overlay-pane, [aria-modal="true"], [role="dialog"], .modal-backdrop, .sheet-backdrop'
    );
    return Array.from(nodes).some(node => {
      // Detached CDK panes and ordinary hover tooltips must not block page navigation.
      if (node.classList.contains('cdk-overlay-pane') &&
        !Array.from(node.children).some(child => child.tagName.toLowerCase() !== 'mat-tooltip-component')) return false;
      const style = this.document.defaultView?.getComputedStyle(node);
      return !!node.getClientRects().length && style?.display !== 'none' && style?.visibility !== 'hidden';
    });
  }

  private readonly captureKeydown = (event: KeyboardEvent): void => {
    if (!this.plainEscape(event)) return;
    // Capture before Material/local handlers close or detach their popup. This same
    // keystroke must never close a dialog and then also leave the underlying page.
    this.keyContexts.set(event, { blocked: this.hasBlockingOverlay(), revision: this.revision });
  };

  private readonly onKeydown = (event: KeyboardEvent): void => {
    const context = this.keyContexts.get(event);
    const window = this.document.defaultView;
    if (!window || !context || context.blocked || event.defaultPrevented || !this.plainEscape(event)) return;
    // Let document/window handlers finish cancelling editing or closing local UI.
    const timer = window.setTimeout(() => {
      this.timers.delete(timer);
      if (event.defaultPrevented || context.revision !== this.revision || this.navigating || this.backPending ||
        this.hasBlockingOverlay() || this.cursor <= 0) return;
      this.backPending = true;
      this.zone.run(() => this.location.back());
    }, 0);
    this.timers.add(timer);
  };

  ngOnDestroy(): void {
    const window = this.document.defaultView;
    window?.removeEventListener('keydown', this.captureKeydown, true);
    window?.removeEventListener('keydown', this.onKeydown);
    this.timers.forEach(timer => window?.clearTimeout(timer));
    this.timers.clear();
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }
}
